/**
 * Care Objective MEASUREMENT model — the contract that turns a Care objective
 * from a qualitative 1–5 rating into a measurable, trackable goal (a KPI-like
 * target the business + talent measure each cycle and track baseline→current→
 * target).
 *
 * An objective's `measure` is authored on the company **catalog** and carried
 * through the Care-strategy cascade (company → program → cohort); the per-talent
 * `care_objectives` row snapshots it + holds the (hybrid) baseline/target; each
 * `care_objective_feedback` entry records a measured `value` (plus the kept
 * legacy `rating` + `comment`).
 *
 * Error messages are i18n keys (resolved at the call site via `t()`).
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════════
// Measure spec — HOW an objective is measured (authored on the catalog).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Measure type:
 *   - `number`    a raw count / quantity (e.g. "number of office visits")
 *   - `scale`     an ordinal scale via `scaleLabels` (e.g. CEFR A1→C2 language)
 *   - `percent`   a 0–100 percentage
 *   - `milestone` a yes/no achievement (value 0 or 1)
 *   - `rating`    the legacy 1–5 satisfaction rating (back-compat default)
 */
export const careMeasureTypeSchema = z.enum(['number', 'scale', 'percent', 'milestone', 'rating']);
export type CareMeasureType = z.infer<typeof careMeasureTypeSchema>;

/** The "good" direction for progress toward the target. */
export const careMeasureDirectionSchema = z.enum(['increase', 'decrease', 'reach']);
export type CareMeasureDirection = z.infer<typeof careMeasureDirectionSchema>;

/** How an objective is measured + its catalog/cohort default baseline/target. */
export const careObjectiveMeasureSchema = z.object({
  type: careMeasureTypeSchema,
  /** Display unit (e.g. "visits", "hours"). Empty → undefined. */
  unit: z
    .string()
    .trim()
    .max(24, { message: 'business.care.objective.errors.unitTooLong' })
    .optional()
    .or(z.literal('').transform(() => undefined)),
  direction: careMeasureDirectionSchema.default('increase'),
  min: z.number().optional(),
  max: z.number().optional(),
  /** Ordinal labels for `scale` (index 0..n-1), e.g. ['A1','A2','B1','B2','C1','C2']. */
  scaleLabels: z.array(z.string().trim().min(1)).max(12).optional(),
  /** Catalog/cohort default baseline + target (the hybrid default — overridable per talent). */
  defaultBaseline: z.number().optional(),
  defaultTarget: z.number().optional(),
});
export type CareObjectiveMeasure = z.infer<typeof careObjectiveMeasureSchema>;

/** A company-catalog objective definition (id + text + category + how it's measured). */
export const careObjectiveCatalogItemSchema = z.object({
  id: z.string().min(1),
  text: z.string().trim().min(1).max(200),
  category: z.string().trim().max(50).optional(),
  /** When absent, the objective is treated as a legacy `rating` measure. */
  measure: careObjectiveMeasureSchema.optional(),
});
export type CareObjectiveCatalogItem = z.infer<typeof careObjectiveCatalogItemSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Per-talent target override (HYBRID) — HR confirms/overrides at onboarding.
// ═══════════════════════════════════════════════════════════════════════════

/** PATCH payload to override one talent's baseline/target for one objective. */
export const careObjectiveTargetOverrideSchema = z
  .object({
    baselineValue: z.number().nullable().optional(),
    targetValue: z.number().nullable().optional(),
  })
  .refine((v) => v.baselineValue !== undefined || v.targetValue !== undefined, {
    message: 'business.care.objective.errors.targetRequired',
  });
export type CareObjectiveTargetOverrideInput = z.infer<typeof careObjectiveTargetOverrideSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Feedback entry — the measured VALUE this cycle (provider or talent self).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * One per-objective measurement for a cycle. `value` is the measured number
 * (raw count / scale index / percent / milestone 0|1); `rating` (1–5) is kept
 * for legacy/soft objectives; `comment` is the optional note. At least one of
 * `value` / `rating` / `comment` must be present.
 */
export const careObjectiveFeedbackEntrySchema = z
  .object({
    objectiveId: z.uuid(),
    value: z.number().finite().optional(),
    rating: z
      .number()
      .int()
      .min(1, { message: 'business.care.objective.errors.ratingRange' })
      .max(5, { message: 'business.care.objective.errors.ratingRange' })
      .optional(),
    comment: z
      .string()
      .trim()
      .max(2000, { message: 'business.care.objective.errors.commentTooLong' })
      .optional()
      .or(z.literal('').transform(() => undefined)),
  })
  .refine((e) => e.value !== undefined || e.rating !== undefined || (e.comment?.length ?? 0) > 0, {
    message: 'business.care.objective.errors.entryEmpty',
  });
export type CareObjectiveFeedbackEntryInput = z.infer<typeof careObjectiveFeedbackEntrySchema>;

/** Submit payload (provider or self): the per-objective entries for the open cycle. */
export const submitCareObjectiveFeedbackSchema = z.object({
  entries: z.array(careObjectiveFeedbackEntrySchema).min(1),
});
export type SubmitCareObjectiveFeedbackInput = z.infer<typeof submitCareObjectiveFeedbackSchema>;
